(ns storrito.connect3.core
  "The connect3 UI. Initial template based on: https://github.com/anteoas/replicant-mini-app/tree/main"
  (:require [cljs-http.client :as http]
            [cljs.core.async :as a]
            [storrito.connect3.start-panel]
            [storrito.connect3.connect-via-private-proxy]
            [storrito.connect3.connect-via-proxy]
            [storrito.connect3.login-panel]
            [storrito.connect3.email-connect-panel]
            [storrito.connect3.select-2fa-method]
            [storrito.connect3.notification-approval-panel]
            [storrito.connect3.two-factor-notification-panel]
            [storrito.connect3.two-factor-panel]
            [storrito.connect3.success-panel]
            [storrito.connect3.select-verify-method-panel]
            [storrito.connect3.change-password-panel]
            [storrito.connect3.submit-phone-panel]
            [storrito.connect3.waiting]
            [storrito.connect3.common :as common]
            [storrito.connect3.device :as device]
            [lambdaisland.uri :as uri]
            [storrito.calendly.links :as calendly]
            [ui.app-frame :as app-frame]
            [ui.user-session :as user-session]
            [storrito.connect3.verify-panel]
            [storrito.connect3.recovery-panel]
            [clojure.string :as str]
            [ui.totp.generate :as totp-gen]
            [app.totp.validation :as validation]
            [storrito.instagram.proxy-error :as proxy-error])
  (:require-macros [cljs.core.async.macros :refer [go]]))

(defn connect-link-banner
  [connect3-state]
  (let [created-by (:connect-link/created-by connect3-state)
        invalid? (:connect-link/invalid? connect3-state)]
    (cond
      invalid?
      [:div.bg-red-50.border-b.border-red-200.px-4.py-3.text-center.text-sm.text-red-800
       "This connect-link is invalid."]

      created-by
      [:div.bg-blue-50.border-b.border-blue-200.px-4.py-3.text-center.text-sm.text-blue-800
       [:span.font-semibold created-by]
       " invited you to connect an Instagram account to their "
       [:a.font-semibold.underline {:href "https://storrito.com/" :target "_blank"} "Storrito"]
       " organization."])))

(defn frame
  [content]
  [:div.overflow-visible.pt-12.pb-12.pl-4.pr-4.flex.items-center.justify-center.w-full
   content])

(defn get-valid-totp-secret
  "Returns a cleaned, valid TOTP secret from the connect3 state, or nil."
  [connect3-state]
  (when-let [raw (:totp-secret connect3-state)]
    (let [secret (validation/clean-secret raw)]
      (when (validation/valid-secret-format? secret)
        secret))))

(def max-auto-totp-retries
  "Max number of auto-TOTP attempts before falling back to manual 2FA entry."
  2)

(defn auto-totp-panel
  "Shown while auto-TOTP is solving the 2FA challenge."
  []
  [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-10.pb-10.max-w-xl.w-full.mx-auto.flex.flex-col.items-center
   [:div.h-16.w-16.rounded-full.bg-purple-50.flex.items-center.justify-center.mb-6
    [:i.fas.fa-robot.text-purple-500.text-2xl]]
   [:h2.text-2xl.font-bold.text-gray-900.mb-2.text-center
    "Automatically solving 2FA"]
   [:p.text-gray-500.text-center.mb-6
    "Using your TOTP secret key to verify automatically..."]
   [:div.flex.items-center.gap-3
    [:div.animate-spin.h-5.w-5.border-2.border-purple-500.border-t-transparent.rounded-full]
    [:span.text-sm.text-gray-600 "Verifying code..."]]])

(defn session-expired-panel
  "Shown when the 10-minute connect3 session has elapsed. The PHP process on
   the server is gone by this point, so the only path forward is to start over
   in a new session."
  []
  [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-10.pb-10.max-w-xl.w-full.mx-auto.text-center
   [:div.h-16.w-16.rounded-full.bg-gray-100.flex.items-center.justify-center.mb-6.mx-auto
    [:i.fas.fa-hourglass-end.text-gray-500.text-2xl]]
   [:h2.text-2xl.font-bold.text-gray-900.mb-2
    "Session expired"]
   [:p.text-gray-500.mb-6
    "This connect session timed out after 10 minutes. Please close this window and start again."]
   [:div.flex.flex-col.gap-3.max-w-xs.mx-auto
    [:a.inline-flex.items-center.justify-center.w-full.bg-blue-600.text-white.px-4.py-3.rounded-md.font-medium.hover:bg-blue-700.transition.cursor-pointer
     {:on {:click [[:connect3/close-window]]}}
     [:i.fas.fa-times.mr-2]
     "Close"]]])

(defn unknown-status-panel
  "Friendly fallback shown when the connect3 backend reports a status the UI
   doesn't have a dedicated panel for. Offers a retry and a support contact.
   For the Instagram walls connect.php reports as `unhandled-exception`
   (see `common/instagram-recovery-wall?`) the title says so instead of
   \"unexpected\" - the copy tells the user to finish the step in the
   Instagram app."
  [state]
  (let [wall? (common/instagram-recovery-wall? state)]
    [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-10.pb-10.max-w-xl.w-full.mx-auto.text-center
     [:div.h-16.w-16.rounded-full.bg-yellow-50.flex.items-center.justify-center.mb-6.mx-auto
      (if wall?
        [:i.fas.fa-shield-alt.text-yellow-500.text-2xl]
        [:i.fas.fa-exclamation-triangle.text-yellow-500.text-2xl])]
     [:h2.text-2xl.font-bold.text-gray-900.mb-2
      (if wall?
        "Instagram needs an extra step for this account"
        "Something unexpected happened")]

     [:p.text-gray-500.mb-6
      (or (common/friendly-error-message state)
          "We ran into a step we couldn't display. Please try again — if this keeps happening, get in touch and we'll help.")]
     [:div.flex.flex-col.gap-3.max-w-xs.mx-auto
      [:a.inline-flex.items-center.justify-center.w-full.bg-blue-600.text-white.px-4.py-3.rounded-md.font-medium.hover:bg-blue-700.transition
       {:href (str "/ui/connect3" js/window.location.search)}
       [:i.fas.fa-redo.mr-2]
       "Try again"]
      [:a.inline-flex.items-center.justify-center.w-full.text-blue-600.px-4.py-2.font-medium.hover:underline.cursor-pointer
       {:on {:click [[:connect3/open-support-chat]]}}
       [:i.fas.fa-comments.mr-2]
       "Contact support"]]
     (when (= js/document.location.hostname "localhost")
       [:div.mt-6.pt-4.border-t.border-gray-200.text-left
        [:p.text-xs.font-mono.text-gray-400
         "status: " (str (:status state))
         (when (:error-message state)
           (str " · " (:error-message state)))]])]))

(defn throttled-panel
  "Shown when Instagram throttles the login attempts for this account. The
   throttle is tied to the Instagram account, not to the connection -
   retrying right away or from a fresh IP does not help, waiting does."
  []
  [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-10.pb-10.max-w-xl.w-full.mx-auto.text-center
   [:div.h-16.w-16.rounded-full.bg-yellow-50.flex.items-center.justify-center.mb-6.mx-auto
    [:i.fas.fa-hourglass-half.text-yellow-500.text-2xl]]
   [:h2.text-2xl.font-bold.text-gray-900.mb-2
    "Instagram is rate-limiting this login"]
   [:p.text-gray-500.mb-6
    "Too many login attempts were made for this Instagram account recently.
     This limit is tied to the account itself, so retrying right away or
     switching networks won't help. Please wait about 15 minutes, then try
     again."]
   [:div.flex.flex-col.gap-3.max-w-xs.mx-auto
    [:a.inline-flex.items-center.justify-center.w-full.bg-blue-600.text-white.px-4.py-3.rounded-md.font-medium.hover:bg-blue-700.transition
     {:href (str "/ui/connect3" js/window.location.search)}
     [:i.fas.fa-redo.mr-2]
     "Try again later"]
    [:a.inline-flex.items-center.justify-center.w-full.text-blue-600.px-4.py-2.font-medium.hover:underline.cursor-pointer
     {:on {:click [[:connect3/open-support-chat]]}}
     [:i.fas.fa-comments.mr-2]
     "Contact support"]]])

(defn security-check-panel
  "Shown when Instagram answered the two-factor step with a security check
   Storrito cannot show (a CAPTCHA, or for `:check` \"limbo\" a screen
   whose content is unknown, see securityCheck.php). The PHP process exits
   after reporting it; the step has to be done in Instagram first."
  [state]
  (let [limbo? (= "limbo" (:check state))]
    [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-10.pb-10.max-w-xl.w-full.mx-auto.text-center
     [:div.h-16.w-16.rounded-full.bg-yellow-50.flex.items-center.justify-center.mb-6.mx-auto
      [:i.fas.fa-shield-alt.text-yellow-500.text-2xl]]
     [:h2.text-2xl.font-bold.text-gray-900.mb-2
      (if limbo?
        "Instagram needs an extra step for this account"
        "Instagram wants a security check")]
     [:p.text-gray-500.mb-6
      (if limbo?
        "Instagram paused this login and asks for an extra step for this account first. Open the Instagram app or instagram.com, log in to this account there and follow the steps Instagram shows. Then connect again here."
        "Instagram asked for a security check (a CAPTCHA) before it continues this login, and Storrito can't show that check. Open the Instagram app or instagram.com, log in to this account there and complete any check Instagram shows. Then connect again here.")]
     [:div.flex.flex-col.gap-3.max-w-xs.mx-auto
      [:a.inline-flex.items-center.justify-center.w-full.bg-blue-600.text-white.px-4.py-3.rounded-md.font-medium.hover:bg-blue-700.transition
       {:href (str "/ui/connect3" js/window.location.search)}
       [:i.fas.fa-redo.mr-2]
       "Try again"]
      [:a.inline-flex.items-center.justify-center.w-full.text-blue-600.px-4.py-2.font-medium.hover:underline.cursor-pointer
       {:on {:click [[:connect3/open-support-chat]]}}
       [:i.fas.fa-comments.mr-2]
       "Contact support"]]]))

(defn account-recovery-panel
  "Shown when Instagram answered the login with its account recovery
   screen (com.bloks.www.ap.last_resort_recovery.limbo, see
   lastResortRecovery.php): the login info is no longer connected to a
   login-able account. The PHP process exits after reporting it - a retry
   or another proxy gets the same screen, the recovery has to be finished
   in the Instagram app. Reconnects of accounts that were connected before
   get it too, so the username is only the first thing to check."
  []
  [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-10.pb-10.max-w-xl.w-full.mx-auto.text-center
   [:div.h-16.w-16.rounded-full.bg-yellow-50.flex.items-center.justify-center.mb-6.mx-auto
    [:i.fas.fa-user-shield.text-yellow-500.text-2xl]]
   [:h2.text-2xl.font-bold.text-gray-900.mb-2
    "Instagram is recovering this account"]
   [:p.text-gray-500.mb-4
    "Instagram says this login info is no longer connected to an account
     and wants to take you through its account recovery first. Retrying
     here gets the same answer."]
   [:ol.text-left.text-gray-600.text-sm.mb-6.mx-auto.max-w-sm.list-decimal.pl-5.space-y-2
    [:li "Check the username - if it has a typo, start again with the right one."]
    [:li "Open the Instagram app or instagram.com, log in to this account there and follow the \"Recover your account\" steps Instagram shows."]
    [:li "Once you are logged in there, connect the account again here."]]
   [:div.flex.flex-col.gap-3.max-w-xs.mx-auto
    [:a.inline-flex.items-center.justify-center.w-full.bg-blue-600.text-white.px-4.py-3.rounded-md.font-medium.hover:bg-blue-700.transition
     {:href (str "/ui/connect3" js/window.location.search)}
     [:i.fas.fa-redo.mr-2]
     "Start again"]
    [:a.inline-flex.items-center.justify-center.w-full.text-blue-600.px-4.py-2.font-medium.hover:underline.cursor-pointer
     {:on {:click [[:connect3/open-support-chat]]}}
     [:i.fas.fa-comments.mr-2]
     "Contact support"]]])

(defn denied-panel
  "Shown when the user tapped Deny / \"This wasn't me\" on the Instagram
   login notification. The PHP process exits after reporting this
   terminal status, so the connect has to be restarted."
  []
  [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-10.pb-10.max-w-xl.w-full.mx-auto.text-center
   [:div.h-16.w-16.rounded-full.bg-red-50.flex.items-center.justify-center.mb-6.mx-auto
    [:i.fas.fa-hand-paper.text-red-500.text-2xl]]
   [:h2.text-2xl.font-bold.text-gray-900.mb-2
    "The login request was denied"]
   [:p.text-gray-500.mb-6
    "The login request was declined in the Instagram app — maybe
     \"This wasn't me\" was tapped by accident. That login attempt is
     closed now. Start over and tap \"Approve\" this time — the request
     comes from Storrito, not an intruder."]
   [:div.flex.flex-col.gap-3.max-w-xs.mx-auto
    [:a.inline-flex.items-center.justify-center.w-full.bg-blue-600.text-white.px-4.py-3.rounded-md.font-medium.hover:bg-blue-700.transition
     {:href (str "/ui/connect3" js/window.location.search)}
     [:i.fas.fa-redo.mr-2]
     "Try again"]
    [:a.inline-flex.items-center.justify-center.w-full.text-blue-600.px-4.py-2.font-medium.hover:underline.cursor-pointer
     {:on {:click [[:connect3/open-support-chat]]}}
     [:i.fas.fa-comments.mr-2]
     "Contact support"]]])

(defn- main-view [state]
  (let [retries (or (::auto-totp-retries state) 0)
        has-auto-totp? (and (some? (get-valid-totp-secret state))
                            (< retries max-auto-totp-retries))]
    [:div
     (connect-link-banner state)
     [:div.pt-6.px-4.text-center
      [:a.inline-flex.items-center.gap-2.rounded-full.bg-blue-50.border.border-blue-200.px-4.py-2.text-sm.text-blue-800.font-medium.hover:bg-blue-100.transition-colors
       {:href (calendly/url :connect-help "connect-session" (:organization/uuid state))
        :target "_blank"}
       [:i.fas.fa-phone]
       "Stuck? Book a 15-minute connect call"]]
     (if (:connect-link/invalid? state)
       (frame
         [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-10.pb-10.max-w-xl.w-full.mx-auto.text-center
          [:div.h-16.w-16.rounded-full.bg-red-50.flex.items-center.justify-center.mb-6.mx-auto
           [:i.fas.fa-exclamation-triangle.text-red-500.text-2xl]]
          [:h2.text-2xl.font-bold.text-gray-900.mb-2 "Invalid connect-link"]
          [:p.text-gray-500
           "This connect-link is no longer valid. Please ask the sender for a new link."]])
       (frame

         (case (:status state)
         "config"
         (storrito.connect3.start-panel/panel state)

         "connect-via-private-proxy"
         (storrito.connect3.connect-via-private-proxy/panel state)

         "connect-via-proxy"
         (storrito.connect3.connect-via-proxy/panel state)

         "login-data"
         (if (:email-connect? state)
           (storrito.connect3.email-connect-panel/panel state)
           (storrito.connect3.login-panel/panel state))

         "select-2fa-method"
         (if has-auto-totp?
           (auto-totp-panel)
           (storrito.connect3.select-2fa-method/panel state))

         "waiting-for-approval"
         (storrito.connect3.notification-approval-panel/panel state)

         ("two-factor-code-required" "two-factor-code-wrong")
         (if has-auto-totp?
           (auto-totp-panel)
           (storrito.connect3.two-factor-panel/panel state))

         ("generic-two-factor-code-required" "generic-two-factor-code-wrong")
         (storrito.connect3.two-factor-panel/generic-panel state)

         "two-factor-notification-required"
         (storrito.connect3.two-factor-notification-panel/panel state)

         "user-tabbed-denied"
         (denied-panel)

         "login-success"
         (storrito.connect3.success-panel/panel state)

         "select-verify-method"
         (storrito.connect3.select-verify-method-panel/panel state)

         "verify-code-required"
         (storrito.connect3.verify-panel/panel state)

         ("recovery-code-required" "recovery-code-wrong")
         (storrito.connect3.recovery-panel/code-panel state)

         "select-recovery-method"
         (storrito.connect3.recovery-panel/method-panel state)

         "recovery-captcha-required"
         (storrito.connect3.recovery-panel/captcha-panel state)

         "change-password"
         (storrito.connect3.change-password-panel/panel state)

         "submit-phone"
         (storrito.connect3.submit-phone-panel/panel state)

         "waiting"
         (storrito.connect3.waiting/panel state)

         "session-expired"
         (session-expired-panel)

         "throttled"
         (throttled-panel)

         "security-check-required"
         (security-check-panel state)

         "account-recovery-required"
         (account-recovery-panel)

         ;; else:
         (unknown-status-panel state)
         )))
     (when (= js/document.location.hostname
              "localhost")
       [:div [:p (pr-str state)]])]))

(defn get-events
  [state]
  (:events (:connect3 state)))

(defn last-event
  [state]
  (last (:events (:connect3 state))))

(def send-timeout-ms
  "The POST returns once the server chose a proxy, and a failing BrightData
   city probe takes up to ~50 s. Since a timeout no longer re-enables the
   form (see `keep-waiting?`), this only bounds a server that went away."
  90000)

(defn keep-waiting?
  "True when a failed POST must not re-enable the form: the server is still
   processing the submission (client-side timeout) or has already queued it
   (409 from its duplicate guard). Re-enabling the form let users submit the
   login data a second time, which the PHP process then read as the answer
   to its next question - a 2FA prompt - and crashed (2026-09 analysis)."
  [response]
  (or (= :timeout (:error-code response))
      (= 409 (:status response))))

(defn clear-stale-sending
  "Once the events moved past the request the browser answered - its answer
   or PHP's next question arrived - the form is no longer sending. `::sending`
   stays, so the answered request cannot be answered twice."
  [state]
  (if (and (:sending? (:connect3 state))
           (not= (::sending (:connect3 state))
                 (last (:events (:connect3 state)))))
    (update state :connect3 dissoc :sending? :slow-start?)
    state))

(defn update-session
  "`state` with `f` applied to the connect3 state of the session `uuid`.
   An answer that arrives after the page moved on to another session
   (a Try again link) leaves that session alone."
  [state uuid f & args]
  (if (= uuid (get-in state [:connect3 :uuid]))
    (apply update state :connect3 f args)
    state))

(defn proxy-send-error-message
  [proxy-url]
  (if (proxy-error/relay-app-proxy? proxy-url)
    proxy-error/relay-app-unreachable-message
    proxy-error/proxy-server-unreachable-message))

(defn send-error-message
  [data response]
  (or (get-in response [:body :error-message])
      (get-in response [:body "error-message"])
      (when (= :timeout (:error-code response))
        (if (seq (:proxy data))
          (proxy-send-error-message (:proxy data))
          "Starting the Instagram login took too long. Please try again."))
      (when (seq (:proxy data))
        (proxy-send-error-message (:proxy data)))
      "Starting the Instagram login failed. Please try again."))

(defn send!
  "Sends user input to the connect3 backend as a response to the current request.

   Prevents double-submit by tracking which request event we've already responded to.
   The function only sends if:
   1. The last event is a 'connect3/request' (server is waiting for user input)
   2. We haven't already sent a response for this specific request event

   Uses the request event itself as the deduplication key (stored in ::sending).
   This naturally resets when a new request arrives from the server. The key
   lives in the session's connect3 state: a new session's first request
   equals the last one's, so a key that outlived its session (a Try again
   link) silently swallowed the next Login click."
  [{:keys [store action]}]
  (let [request-event (last-event @store)
        data (second action)
        uuid (get-in @store [:connect3 :uuid])]
    (when (and (not= (::sending (:connect3 @store))
                     request-event)
               (= (:event/kind request-event)
                  "connect3/request"))
      (swap! store update :connect3
             (fn [connect3]
               (-> connect3
                   (assoc ::sending request-event
                          :sending? true)
                   (dissoc :error-message :slow-start?))))
      (go
        (let [response (a/<! (http/post (str "/connect3/instagram-client/" uuid)
                                        {:json-params data
                                         :as :json
                                         :timeout send-timeout-ms}))]
          (cond
            (:success response)
            (swap! store update-session uuid dissoc :sending?)

            (keep-waiting? response)
            (swap! store update-session uuid assoc :slow-start? true)

            :else
            (swap! store update-session uuid
                   (fn [connect3]
                     (-> connect3
                         (dissoc ::sending :sending?)
                         (assoc :error-message
                                (send-error-message data response)))))))))))

(defn send-data!
  "Like send! but takes data directly instead of extracting from an action."
  [store data]
  (send! {:store store
          :action [nil data]}))

(defn recovery-login-data
  "The answer to a rejected login-data request that opts into the code
   login (handleRecovery.php) instead of retrying the password: the
   login panel's data plus the `:recovery` flag connect.php branches on.
   The server (`storrito.connect3.proxy-rotation`) does not count it as
   a password retry."
  [connect3-state]
  (assoc (common/login-data connect3-state)
         :recovery true))

(defn start-recovery!
  [{:keys [store]}]
  (send-data! store (recovery-login-data (:connect3 @store))))

(def ^:private auto-totp-statuses
  #{"select-2fa-method"
    "two-factor-code-required"
    "two-factor-code-wrong"
    "generic-two-factor-code-required"
    "generic-two-factor-code-wrong"})

(def ^:private auto-totp-wrong-statuses
  #{"two-factor-code-wrong" "generic-two-factor-code-wrong"})

(defn auto-totp-snapshot
  "Reads the auto-TOTP state and increments the retry counter when the
   current status is a failed 2FA attempt. Returns a snapshot map with
   :secret and :status, or nil if auto-TOTP should not act.

   Read-then-swap is race-free since JavaScript is single-threaded. Only
   swaps when the counter actually changes — every store swap triggers a
   render, so an unconditional swap here would re-render on every poll."
  [store]
  (let [connect3 (:connect3 @store)
        secret (get-valid-totp-secret connect3)
        status (:status (:data (last (:events connect3))))
        retries (or (::auto-totp-retries connect3) 0)]
    (when (and secret
               (< retries max-auto-totp-retries)
               (contains? auto-totp-statuses status))
      (when (contains? auto-totp-wrong-statuses status)
        (swap! store update-in [:connect3 ::auto-totp-retries] (fnil inc 0)))
      {:secret secret :status status})))

(defn auto-totp!
  "If the user provided a TOTP secret and the current status is a 2FA challenge,
   automatically generate an OTP and send it to the PHP process.
   Falls back to manual entry after max-auto-totp-retries failed attempts."
  [store]
  (when-let [{:keys [secret status]} (auto-totp-snapshot store)]
    (if-let [otp (totp-gen/generate-otp secret)]
      (case status
        "select-2fa-method"
        (send-data! store {:2fa-verification-method "totp"
                           :twofacode otp})

        ("two-factor-code-required" "two-factor-code-wrong"
         "generic-two-factor-code-required" "generic-two-factor-code-wrong")
        (send-data! store {:twofacode otp}))
      ;; OTP generation failed — exhaust retries so the manual 2FA panel shows
      (swap! store assoc-in [:connect3 ::auto-totp-retries] max-auto-totp-retries))))

(defn save!
  [{:keys [store]}]
  (let [last-event-data (:data (last (:events (:connect3 @store))))
        uuid (get-in @store [:connect3 :uuid])]
    (when (and
            (= (:status last-event-data)
               "login-success")
            (not (get-in @store
                         [:connect3
                          :saving])))
      (swap! store
             assoc-in
             [:connect3
              :saving]
             true)
      (go
        (let [response (a/<! (http/post
                               "/connect3/save"
                               {:transit-params (merge
                                                  (select-keys (:connect3 @store)
                                                               [:proxy :totp-secret])
                                                  last-event-data
                                                  (when-let [cl (:connect-link (:connect3 @store))]
                                                    {:connect-link-uuid cl}))}))
              folder-uuid (get-in response [:body :auto-fill/folder-uuid])
              profile-picture-url (get-in response [:body :profile-picture-url])
              start-credit-amount-formatted (get-in response [:body :free-plan.trial/start-credit-amount-formatted])]
          ;; Each answer goes to the session that saved: a Try again link
          ;; may have moved the page on to another one meanwhile.
          (when folder-uuid
            (swap! store
                   update-session uuid
                   assoc :auto-fill/folder-uuid folder-uuid))
          (when profile-picture-url
            (swap! store
                   update-session uuid
                   assoc :profile-picture-url profile-picture-url))
          (when start-credit-amount-formatted
            (swap! store
                   update-session uuid
                   assoc :free-plan.trial/start-credit-amount-formatted
                   start-credit-amount-formatted))
          ;; a refused save (e.g. no account id, see
          ;; storrito.connect3.save/require-account-id) must not leave the
          ;; success panel standing. Setting :status here did nothing:
          ;; `current-state` merges the last PHP event over the store, and
          ;; that event still says login-success. `render` turns :save-error
          ;; into the "save-failed" status, which has no panel of its own
          ;; and renders the unknown-status panel with the message and a
          ;; retry.
          (when-let [error (get-in response
                                   [:body
                                    :error])]
            (swap! store
                   update-session uuid
                   assoc :save-error error)))))))

(def poll-timeout-ms
  "Stop polling after 10 minutes. The PHP process has a 10-minute timeout,
   so there is no reason to keep polling beyond that. This prevents abandoned
   browser tabs from restarting the PHP process indefinitely."
  (* 10 60 1000))

(defn current-poll?
  "True while `poll-id` is the page's current poll loop. False once a
   newer loop took over or the page moved on to another session
   (`session-state` drops the id)."
  [state poll-id]
  (and (:connect3 state)
       (= poll-id (get-in state [:connect3 ::poll-id]))))

(defn poll!
  [{:keys [store] :as params}]
  ;; The page's :on-load actions can run more than once (hot reload in dev,
  ;; re-navigation), which used to stack multiple concurrent poll loops.
  ;; Each loop remembers its own id and exits once a newer loop took over.
  (let [poll-id (random-uuid)]
    (swap! store assoc-in [:connect3 ::poll-id] poll-id)
    (go
      (let [started-at (.now js/Date)]
        (loop []
          (when (current-poll? @store poll-id)
            (let [response (a/<! (http/get (str "/connect3/instagram-client/"
                                                (get-in @store
                                                        [:connect3
                                                         :uuid]))
                                           {:as :json}))]
              ;; Checked again after the answer: the events of a session
              ;; the page left (a Try again link while the request was
              ;; open) must not land in the new one, nor be answered by
              ;; auto-totp! against it.
              (when (current-poll? @store poll-id)
                (swap! store
                       (fn [state]
                         (-> state
                             (assoc-in [:connect3 :events] (:body response))
                             (clear-stale-sending))))
                (auto-totp! store)
                (cond
                  ;; Server is authoritative: 410 Gone means the PHP process is dead.
                  (= 410 (:status response))
                  (swap! store assoc-in [:connect3 :status] "session-expired")

                  ;; Login finished — save! handles the rest, stop polling.
                  (save! params)
                  nil

                  ;; Fallback in case the server never reports 410 (e.g. network).
                  (>= (- (.now js/Date) started-at) poll-timeout-ms)
                  (swap! store assoc-in [:connect3 :status] "session-expired")

                  :else
                  (do (a/<! (a/timeout (+ 1000
                                          (rand-int 500))))
                      (recur)))))))))))

(defn validate-twofacode*
  [state]
  (let [use-back-up (= "backup_codes"
                       (:2fa-verification-method state))
        digits-count (if use-back-up 8 6)
        valid (boolean (re-matches (re-pattern (str "^[0-9]{" digits-count "}"))
                                   (or (:twofacode state)
                                       "")))]
    (assoc state
           :twofacode-valid
           valid
           :error-message
           (when-not valid
             (str "The "
                  (if use-back-up "backup code" "2FA code")
                  " must be "
                  digits-count
                  " digits.")))))

(defn validate-twofacode
  [{:keys [store]}]
  (swap! store
         update
         :connect3
         validate-twofacode*))

(defn get-geo-location-data
  "Requests the client's geo location data from the geo-location-server.

   Returns `:country`, `:city`, `:latitude` and `:longitude`. The backend uses
   these fields to probe the best BrightData city for the user's location via
   `storrito.connect3.proxy-city-probe`."
  []
  (go
    (let [geo-data (:geo.location/data
                    (:body
                     (a/<! (http/get
                             "https://geo-location-server-dot-vire-1315.appspot.com/"
                             {:with-credentials? false}))))]
      {:country (:geo.location.country/code geo-data)
       :city (:geo.location.city/name geo-data)
       :latitude (:geo.location/latitude geo-data)
       :longitude (:geo.location/longitude geo-data)})))

(defn set-geo-location-data
  "Preselects the country in the connect-via-proxy panel based on the user's geo location."
  [{:keys [store]}]
  (go
    (let [geo-data (a/<! (get-geo-location-data))]
      (swap! store
             update
             :connect3
             ;; Only set geo data if the user hasn't already selected a country,
             ;; since the geo endpoint may respond after the user made a selection.
             (fn [connect3]
               (if (:country connect3)
                 connect3
                 (merge connect3 geo-data)))))))

(defn fetch-connect-link-info!
  "When a connect-link UUID is present in the connect3 state, fetches
   organization info from the public /connect-link/info endpoint."
  [{:keys [store]}]
  (when-let [cl-uuid (:connect-link (:connect3 @store))]
    (go
      (let [uuid (:uuid (:connect3 @store))
            response (a/<! (http/get (str "/connect-link/info/" cl-uuid)))]
        (swap! store
               update-session uuid
               (fn [connect3]
                 (let [body (:body response)]
                   (if (:connect-link/valid? body)
                     (assoc connect3
                            :connect-link/created-by
                            (:connect-link/created-by body))
                     (assoc connect3
                            :connect-link/invalid? true)))))))))

(defn fetch-device-label!
  "On a reconnect, fetches the device the account presents to Instagram
   (`storrito.connect3.device-label`): a legacy account keeps its Galaxy
   S20. Until it answers, and when it fails, the panels show
   `device/pending-label`. `reconnect=true` (the reconnect banner) is a
   new connect to the server (`resolve-device-string`), so it fetches
   nothing. Connect links carry no `reconnect`; the route requires a
   login their external users do not have."
  [{:keys [store]}]
  (when-let [reconnect (re-matches #"\d+" (str (:reconnect (:connect3 @store))))]
    (swap! store assoc-in [:connect3 :device-label] device/pending-label)
    (go
      (let [uuid (:uuid (:connect3 @store))
            response (a/<! (http/get "/connect3/device-label"
                                     {:query-params {:reconnect reconnect}}))]
        (when-let [label (and (:success response)
                              (:connect3.device/label (:body response)))]
          (swap! store update-session uuid assoc :device-label label))))))

(defn extract-uuid
  [active-url]
  (parse-uuid
    (str/replace active-url
                 "/ui/connect3/"
                 "")))

(defn session-state
  "The connect3 state for the session `uuid`, opened with the URL's
   `query`. Another session than the one in `connect3` (the page is
   reached without a reload, e.g. by a Try again link) starts afresh: its
   `:reconnect`, events, typed data and device label must not leak into
   the new one. Only a hidden trust banner stays hidden."
  [connect3 uuid query]
  (merge (if (= uuid (:uuid connect3))
           connect3
           (when (false? (:show-trust-info? connect3))
             {:show-trust-info? false}))
         query
         {:status "waiting"
          :uuid uuid
          :proxy ""}))

(defn init
  [{:keys [store] :as params}]
  (swap! store
         (fn [state]
           (-> state
               (assoc :ui/hide-side-menu? true)
               (update :connect3 session-state
                       (extract-uuid js/window.location.pathname)
                       (-> js/window.location.href
                           (uri/uri)
                           (uri/query-map))))))
  (set-geo-location-data params)
  (fetch-connect-link-info! params)
  (fetch-device-label! params)
  ;; Hide Crisp support chat for connect-link users — they are external
  ;; and not Storrito customers, so the chat widget would be confusing.
  (when (:connect-link (:connect3 @store))
    (when (exists? js/window.$crisp)
      (.push js/window.$crisp #js ["do" "chat:hide"]))))

(defn open-support-chat!
  "Reveals and opens the Crisp chat widget. `chat:show` is needed because the
   widget is hidden for connect-link users in `init`."
  [_params]
  (when (exists? js/window.$crisp)
    (.push js/window.$crisp #js ["do" "chat:show"])
    (.push js/window.$crisp #js ["do" "chat:open"])))

(defn close-window!
  [_params]
  (.close js/window))

(defn waiting?
  [state]
  (or (empty? (get-events state))
      (= (:event/kind (last-event state))
         "connect3/response")))

(defn current-state
  [state]
  (let [event-data (:data (last-event state))]
    (merge
      (:connect3 state)
      ;; The TOTP preview countdown in the login panel derives from
      ;; :ui/clock, which the :totp/refresh interval ticks once per second:
      (select-keys state [:ui/clock])
      (when (map? event-data)
        (dissoc event-data :twofacode)))))

(defn connect-link-page
  "The page around the connect-link flow, which shows no navbar and no
   sidebar. `html`, `body` and `#app` are fixed to the viewport height with
   `overflow: hidden` (src/css/style.css), so the page itself never
   scrolls; the app-frame's `main` is the scroll container of every other
   page. This wrapper takes that role here — without it, everything below
   the fold is out of reach, e.g. the confirm button of the approval panels
   on a phone (brain-36)."
  [content]
  [:div.h-full.overflow-y-auto
   content])

(defn render
  [state]
  (let [content (main-view
                  ;; The org uuid goes into the call CTAs' booking links.
                  ;; It is nil on the connect-link page, which external
                  ;; users open without a session.
                  (let [val (assoc (current-state state)
                                   :organization/uuid
                                   (user-session/org-uuid state))]
                    (cond
                      (:save-error (:connect3 state))
                      (assoc val
                             :status "save-failed"
                             :error-message (:save-error (:connect3 state)))

                      (= "session-expired" (:status (:connect3 state)))
                      (assoc val :status "session-expired")

                      (waiting? state)
                      (assoc val :status "waiting")

                      :else val)))]
    (if (:connect-link (:connect3 state))
      (connect-link-page content)
      (app-frame/app-frame state content))))

(comment
  #_(def state
      @(:ui/store @ui.core/system))
  )

(def register
  [{:page-id :pages/connect3-redirect
    :route [["ui" "connect3"]]
    :on-load (fn [_location]
               (let [new-url (str js/window.location.pathname
                                  "/"
                                  (random-uuid)
                                  js/window.location.search)]
                 (.replaceState js/history nil "" new-url)
                 [[:connect3/init]
                  [:connect3/poll]]))
    :render #'render}
   {:page-id :pages/connect3
    :route [["ui" "connect3" :uuid]]
    :on-load (fn [_location]
               [[:connect3/init]
                [:connect3/poll]])
    :render #'render}
   {:ui.action/kind :connect3/poll
    :ui.action/handler #'poll!}
   {:ui.action/kind :connect3/open-support-chat
    :ui.action/handler #'open-support-chat!}
   {:ui.action/kind :connect3/close-window
    :ui.action/handler #'close-window!}])
